
import {
  Combobox, Transition, Switch,
  ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption
} from "@headlessui/react";
import EmfSettingIcon from './icons/settings';
import { Fragment, useEffect, useRef, useState } from "react";
import EmfFilterIcon from './icons/filter';
import EmfRefreshIcon from './icons/refresh';
import EmfExpandIcon from './icons/expand';
import EmfCollapseIcon from './icons/collapse';
import EmfVisualizationIcon from './icons/visualization';
import { EmfWidgetFilterGroup, EmfWidgetFilterItem, EmfWidgetThemeType, IEmfWidget } from "./widget.models";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import SimpleBar from "simplebar-react";
import utils, { getThemeStyle, getUniqueThemeClass } from "../../utils";
import { Popover, PopoverContent, PopoverTrigger } from "../_shared/popover";

const WidgetFilterMenu = (props: IEmfWidget) => {

  if (!props) { return; }
  const widget = props;
  const filters = widget.filters;
  const [filterSelected, setFilterSelected] = useState(false);
  let filterItems = filters as EmfWidgetFilterGroup[];
  const [filterStateChange, setFilterStateChange] = useState<any>({});
  const uniqueThemeClassName = getUniqueThemeClass();
  const buttonRef = useRef<HTMLButtonElement>();

  let handleBoolean = (e: React.ChangeEvent<HTMLInputElement>, item: EmfWidgetFilterItem, allItems: EmfWidgetFilterItem[]) => {
    item.selected = e.target.checked;
    if (item.value == 'all') {
      if (item.selected) {
        allItems.forEach(i => i.selected = true);
      } else {
        allItems.forEach(i => i.selected = false);
      }
    } else {
      let allchecked = allItems.filter(f => f.selected && f.id != 'all');
      let all = allItems.find(f => f.id == 'all');
      if (all) {
        all.selected = ((allItems.length - 1) == allchecked.length);
      }
    }
    setFilterStateChange({ ...item });
    updateFilterStatus();
  }

  let handleDateRange = (e: React.ChangeEvent<HTMLInputElement>, item: EmfWidgetFilterItem) => {
    item.value = e.target.value;
    item.selected = e.target.value ? true : false;
    setFilterStateChange({ ...item });
    updateFilterStatus();
  }

  let onApplyClick = (filters) => {
    widget.onFiltersApplied && widget.onFiltersApplied(filters);
    updateFilterStatus();
  }

  let onCloseClick = () => {
    widget.onFiltersClose && widget.onFiltersClose();
    updateFilterStatus();
  }

  let updateFilterStatus = () => {
    const anyFilterSelected = filterItems.some(i => i.isAnySelected());
    setFilterSelected(anyFilterSelected);
  }

  let compareOrder = (a: EmfWidgetFilterGroup, b: EmfWidgetFilterGroup) => {
    if (a.order < b.order) {
      return -1;
    }
    if (a.order > b.order) {
      return 1;
    }
    return 0;
  }

  const [popoverState, setPopoverState] = useState(false);
  const onPopoverStateChange = (state: boolean) => {
    setPopoverState(state);
  }

  return (
    <Popover>
      <PopoverTrigger onStateChange={onPopoverStateChange} ref={buttonRef} className={`emf-widget-action-icon hover:emf-text-primary focus:emf-outline-none ${widget.config.theme == 'dark' ? 'emf-text-white' : ''}`}>
        <EmfFilterIcon title="Filter" className={`emf-h-4 emf-w-4 ${(filterSelected || popoverState) ? 'emf-text-primary' : ''} hover:emf-text-primary focus:emf-text-primary focus-visible:emf-text-primary`} />
      </PopoverTrigger>
      <PopoverContent
        className={`emf-absolute  emf-right-[8%] emf-widget-action-panel emf-w-80 emf-z-[1501] emf-rounded-sm emf-shadow-custom ${widget.config.theme == 'dark' ? 'emf-widget-popover-menu-dark emf-bg-dark-100' : 'emf-bg-white'}`}>
        <>
          <style>{getThemeStyle(uniqueThemeClassName, widget.config.primaryColor)}</style>
          <div className={`emf-widget-action-panel-container emf-grid emf-grid-cols-1 emf-py-4 ${uniqueThemeClassName}`}>
            <div className="emf-widget-action-panel-header">Filters</div>
            <SimpleBar className="!emf-m-0 !emf-p-0 emf-widget-action-panel-body" forceVisible={"y"} autoHide={false}>
              <div className="emf-space-y-6 emf-items-center emf-h-auto emf-px-4">
                {
                  filterStateChange && filterItems?.sort(compareOrder).filter(f => f.active).map(f => (
                    <div key={utils.getUniqueId()} className="emf-flex emf-flex-col emf-flex-wrap">
                      <div className="flex emf-font-medium emf-text-muted">
                        {f.label}
                      </div>
                      {
                        f.controlType == 'dropdown' &&
                        <WidgetCombobox group={f} theme={widget.config.theme} />
                      }
                      {
                        (f.controlType == 'checkbox' || f.controlType == 'radio') &&
                        <div className="emf-flex emf-flex-wrap emf-items-center emf-mt-2 emf-gap-y-1 emf-gap-x-4">
                          {
                            f.items.map((m: EmfWidgetFilterItem) => (
                              m.label && <label key={m.id} className="emf-flex emf-items-center emf-mb-0">
                                <input className="emf-w-4 emf-h-4 emf-accent-checked" value={m.id} checked={m.selected} type={f.controlType} name={m.label} onChange={(e) => handleBoolean(e, m, f.items)} />
                                <span className="emf-ml-1">{m.label}</span>
                              </label>
                            ))
                          }
                        </div>
                      }
                      {
                        f.controlType == 'daterange' &&
                        <div className="emf-grid emf-grid-cols-2 emf-gap-4">
                          {
                            f.items.map((m: EmfWidgetFilterItem) => (
                              m.label && <div key={m.id} className="emf-flex emf-flex-col emf-items-start">
                                <div className="emf-text-sm">{m.label}</div>
                                <input type="date"
                                  value={m.value ?? null}
                                  onChange={(e) => handleDateRange(e, m)}
                                  className={`${widget.config.theme == 'dark' ? '!emf-text-gray-300 emf-bg-dark-100 [color-scheme:dark]' : 'emf-bg-white'} emf-border-x-0 emf-border-t-0 emf-border-b !emf-outline-none emf-border-gray-300 emf-text-sm emf-block emf-w-full emf-pb-2.5 emf-pt-1`} placeholder="Select date" />
                              </div>
                            ))
                          }
                        </div>
                      }
                    </div>
                  ))
                }
              </div>
            </SimpleBar>
            {
              <div className="emf-widget-action-panel-footer emf-text-right">
                <button
                  onClick={() => { buttonRef?.current?.click(); onCloseClick(); }}
                  className={`emf-btn-action-panel emf-mx-4 emf-text-sm ${widget.config.theme == 'dark' ? 'emf-bg-dark-200 emf-text-secondary' : 'emf-bg-gray-300'}`}>
                  {props.config?.actionPanel?.filters?.cancelText ?? 'CLOSE'}
                </button>
                <button onClick={() => { buttonRef?.current?.click(); onApplyClick(filterItems); }}
                  className="emf-btn-action-panel emf-bg-primary emf-text-secondary emf-shadow-sm emf-text-sm">
                  {props.config?.actionPanel?.filters?.okText ?? 'APPLY'}
                </button>
              </div>
            }
          </div>
        </>
      </PopoverContent>
    </Popover>
  )
}

const WidgetVisualizationMenu = (props: IEmfWidget) => {

  if (!props) { return; }
  const widget = props;
  const filters = widget.filters;
  const [filterSelected, setFilterSelected] = useState(false);
  let filterItems = filters as EmfWidgetFilterGroup[];
  const [filterStateChange, setFilterStateChange] = useState<any>({});
  const uniqueThemeClassName = getUniqueThemeClass();
  const buttonRef = useRef<HTMLButtonElement>();

  let onApplyClick = (filters) => {
    widget.onFiltersApplied && widget.onFiltersApplied(filters);
    updateFilterStatus();
  }

  let onCloseClick = () => {
    widget.onFiltersClose && widget.onFiltersClose();
    updateFilterStatus();
  }

  let updateFilterStatus = () => {
    const anyFilterSelected = filterItems.some(i => i.isAnySelected());
    setFilterSelected(anyFilterSelected);
  }

  let compareOrder = (a: EmfWidgetFilterGroup, b: EmfWidgetFilterGroup) => {
    if (a.order < b.order) {
      return -1;
    }
    if (a.order > b.order) {
      return 1;
    }
    return 0;
  }

  const [popoverState, setPopoverState] = useState(false);
  const onPopoverStateChange = (state: boolean) => {
    setPopoverState(state);
  }

  return (
    <Popover>
      <PopoverTrigger onStateChange={onPopoverStateChange} ref={buttonRef} className={`emf-widget-action-icon hover:emf-text-primary focus:emf-outline-none ${widget.config.theme == 'dark' ? 'emf-text-white' : ''}`}>
        <EmfVisualizationIcon className={`emf-h-6 emf-w-6 ${(filterSelected || popoverState) ? 'emf-text-primary' : ''} hover:emf-text-primary focus:emf-text-primary focus-visible:emf-text-primary`} />
      </PopoverTrigger>
      <PopoverContent
        className={`emf-absolute emf-widget-action-panel emf-w-80 emf-z-[1501] emf-rounded-sm emf-shadow-custom ${widget.config.theme == 'dark' ? 'emf-widget-popover-menu-dark emf-bg-dark-100' : 'emf-bg-white'}`}>
        <>
          <style>{getThemeStyle(uniqueThemeClassName, widget.config.primaryColor)}</style>
          <div className={`emf-widget-action-panel-container emf-grid emf-grid-cols-1 emf-py-4 ${uniqueThemeClassName}`}>
            <div className="emf-widget-action-panel-header">Filters</div>
            <SimpleBar className="!emf-m-0 !emf-p-0 emf-widget-action-panel-body" forceVisible={"y"} autoHide={false}>
              <div className="emf-space-y-6 emf-items-center emf-h-auto emf-px-4">
                {
                  filterStateChange && filterItems?.sort(compareOrder).filter(f => f.active).map(f => (
                    <div key={utils.getUniqueId()} className="emf-flex emf-flex-col emf-flex-wrap">
                      <div className="flex emf-font-medium emf-text-muted">
                        {f.label}
                      </div>
                      {
                        f.controlType == 'dropdown' &&
                        <WidgetCombobox group={f} theme={widget.config.theme} />
                      }
                    </div>
                  ))
                }
              </div>
            </SimpleBar>
            {
              <div className="emf-widget-action-panel-footer emf-text-right">
                <button
                  onClick={() => { buttonRef?.current?.click(); onCloseClick(); }}
                  className={`emf-btn-action-panel emf-mx-4 emf-text-sm ${widget.config.theme == 'dark' ? 'emf-bg-dark-200 emf-text-secondary' : 'emf-bg-gray-300'}`}>
                  {props.config?.actionPanel?.visualizations?.cancelText ?? 'CLOSE'}
                </button>
                <button onClick={() => { buttonRef?.current?.click(); onApplyClick(filterItems); }}
                  className="emf-btn-action-panel emf-bg-primary emf-text-secondary emf-shadow-sm emf-text-sm">
                  {props.config?.actionPanel?.visualizations?.okText ?? 'APPLY'}
                </button>
              </div>
            }
          </div>
        </>
      </PopoverContent>
    </Popover>
  )
}

const WidgetSettingsMenu = (props: IEmfWidget) => {
  const widget = props;
  const uniqueThemeClassName = getUniqueThemeClass();
  const buttonRef = useRef<HTMLButtonElement>();

  let onApplyClick = (filters) => {
    widget.onFiltersApplied && widget.onFiltersApplied(filters);
  }

  let onCloseClick = () => {
    widget.onSettingsClose && widget.onSettingsClose();
  }

  const [popoverState, setPopoverState] = useState(false);
  const onPopoverStateChange = (state: boolean) => {
    setPopoverState(state);
  }

  return (

    <Popover>
      <PopoverTrigger onStateChange={onPopoverStateChange} ref={buttonRef} className={`emf-widget-action-icon hover:emf-text-primary focus:emf-outline-none ${widget.config.theme == 'dark' ? 'emf-text-white' : ''}`}>
        <EmfSettingIcon className={`emf-h-4 emf-w-4 ${(popoverState) ? 'emf-text-primary' : ''} hover:emf-text-primary focus:emf-text-primary focus-visible:emf-text-primary`} />
      </PopoverTrigger>
      <PopoverContent
        className={`emf-widget-action-panel emf-w-80 emf-z-[1501] emf-rounded-sm emf-shadow-custom ${widget.config.theme == 'dark' ? 'emf-widget-popover-menu-dark emf-bg-dark-100' : 'emf-bg-white'}`}>
        <>
          <style>{getThemeStyle(uniqueThemeClassName, widget.config.primaryColor)}</style>
          <div className={`emf-widget-action-panel-container emf-grid emf-grid-cols-1 emf-py-4 ${uniqueThemeClassName}`}>
            <div className="emf-widget-action-panel-header">Settings</div>
            <SimpleBar className="!emf-m-0 !emf-p-0 emf-widget-action-panel-body" forceVisible={"y"} autoHide={false}>
              <div key={utils.getUniqueId()} className="emf-space-y-6 emf-items-center emf-h-auto emf-px-4">
                {
                  widget.settings?.map((item: any, idx: any) => (
                    <WidgetSettingMenuItem key={idx} item={item} onSelectedChange={(s: boolean) => item.enabled = s} />
                  ))}

              </div>
            </SimpleBar>
            {
              <div className="emf-widget-action-panel-footer emf-text-right">
                <button
                  onClick={() => { buttonRef?.current?.click(); onCloseClick(); }}
                  className={`emf-btn-action-panel emf-mx-4 emf-text-sm ${widget.config.theme == 'dark' ? 'emf-bg-dark-200 emf-text-secondary' : 'emf-bg-gray-300'}`}>
                  {props.config?.actionPanel?.settings?.cancelText ?? 'CLOSE'}
                </button>
                <button onClick={() => { buttonRef?.current?.click(); onApplyClick(widget.settings); }}
                  className="emf-btn-action-panel emf-bg-primary emf-text-secondary emf-shadow-sm emf-text-sm">
                  {props.config?.actionPanel?.settings?.okText ?? 'SAVE'}
                </button>
              </div>
            }
          </div>
        </>
      </PopoverContent>
    </Popover>
  )
}

const WidgetSettingMenuItem = ({ item, onSelectedChange }: any): any => {
  const [enabled, setEnabled] = useState(item.enabled);
  useEffect(() => { onSelectedChange(enabled) }, [enabled])
  return (
    <div className="emf-flex emf-items-center !emf-my-2">
      <Switch
        id={"emf-legent-switch-" + item.id}
        checked={enabled}
        onChange={setEnabled}
        className={`${enabled ? 'emf-bg-primary' : 'emf-bg-gray-400'}
                       emf-relative emf-px-0 emf-mr-2 emf-inline-flex emf-h-[19px] emf-w-[32px] 
                       emf-shrink-0 emf-cursor-pointer emf-rounded-full emf-border-2 emf-border-transparent
                       emf-transition-colors emf-duration-200 emf-ease-in-out focus:emf-outline-none 
                       focus-visible:emf-ring-2  focus-visible:emf-ring-white/75`}>
        <span className="emf-sr-only">Use setting</span>
        <span
          aria-hidden="true"
          className={`${enabled ? 'emf-translate-x-4' : 'emf-translate-x-0'}
                         emf-pointer-events-none emf-inline-block emf-h-[14px] emf-w-[14px] 
                         emf-transform emf-rounded-full emf-bg-white emf-shadow-lg emf-ring-0 
                         emf-transition emf-duration-200 emf-ease-in-out`}
        />
      </Switch>
      <label className="emf-flex emf-cursor-pointer emf-p-0 !emf-my-0" htmlFor={"emf-legent-switch-" + item.id}>{item.name}</label>
    </div>
  )
}

const WidgetRefreshMenu = (props: IEmfWidget) => {
  let widget = props;
  let onRefreshClick = () => {
    widget.onRefresh && widget.onRefresh(true);
  }
  return (
    <button onClick={() => { onRefreshClick(); }} className={`emf-widget-action-icon hover:emf-text-primary focus:emf-outline-none ${widget.config.theme == 'dark' ? 'emf-text-white' : ''}`}>
      <EmfRefreshIcon title="Refresh" className="emf-h-4 emf-w-4 hover:emf-text-primary focus:emf-text-primary focus-visible:emf-text-primary" />
    </button>
  )
}

const WidgetCombobox = ({ group, theme }) => {
  let dataModel = group as EmfWidgetFilterGroup;
  const selectedItems = dataModel.items.filter(f => f.selected && f.id != 'all');

  const [filterStateChange, setFilterStateChange] = useState<any>({});

  let handleBoolean = (e: React.ChangeEvent<HTMLInputElement>, item: EmfWidgetFilterItem) => {
    item.selected = e.target.checked;
    if (item.value == 'all') {
      if (item.selected) {
        dataModel.items.forEach(i => i.selected = true);
      } else {
        dataModel.items.forEach(i => i.selected = false);
      }
    } else {
      let allchecked = dataModel.items.filter(f => f.selected && f.id != 'all');
      let all = dataModel.items.find(f => f.id == 'all');
      if (all) {
        all.selected = ((dataModel.items.length - 1) == allchecked.length);
      }
    }
    setFilterStateChange({ ...item });
  }

  const [query, setQuery] = useState('')

  const filteredItems =
    query === ''
      ? dataModel.items
      : dataModel.items.filter((i: any) =>
      (i.label?.toLowerCase()
        .replace(/\s+/g, '')
        .includes(query.toLowerCase().replace(/\s+/g, '')
          || i.name?.toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, '')))
      ));

  return (
    <Combobox multiple value={selectedItems} onClose={() => setQuery('')}>
      <div className={`emf-w-full emf-relative ${theme == 'dark' ? 'emf-bg-dark-100' : ''}`}>
        <div className="emf-border-b emf-border-x-0 emf-border-t-0 emf-border-solid emf-border-gray-400 emf-relative emf-w-full emf-cursor-default emf-overflow-hidden !emf-outline-none ">
          <ComboboxButton className="emf-border-none !emf-outline-none !emf-p-0 emf-w-full">
            <ComboboxInput
              placeholder={selectedItems.length > 0 ? selectedItems.length + ' item(s) selected' : dataModel.placeholder}
              className={`emf-w-full emf-py-2 emf-pr-8 emf-text-sm emf-leading-5 emf-text-gray-900 !emf-outline-none emf-border-none ${theme == 'dark' ? 'emf-bg-dark-100' : ''}`}
              onChange={(event) => setQuery(event.target.value)}
            />
          </ComboboxButton>
          <ComboboxButton className={`emf-absolute emf-inset-y-0 emf-right-0 emf-pr-0 emf-flex emf-items-center emf-border-none ${theme == 'dark' ? 'emf-bg-dark-100' : 'emf-bg-white'}`}>
            <ChevronDownIcon className="emf-h-4 emf-w-4 emf-text-gray-400" aria-hidden="true" />
          </ComboboxButton>
        </div>
        <Transition
          as={Fragment}
          leave="emf-transition emf-ease-in emf-duration-100"
          leaveFrom="emf-opacity-100"
          leaveTo="emf-opacity-0"
          afterLeave={() => setQuery('')}
        >
          <ComboboxOptions modal={false} className={`emf-ul-li-list-none emf-absolute emf-mt-1 emf-max-h-60 emf-w-full emf-overflow-auto emf-rounded-md emf-py-1 emf-text-base emf-shadow-lg emf-ring-1 emf-ring-black/5 focus:emf-outline-none sm:emf-text-sm ${theme == 'dark' ? 'emf-bg-dark-50' : 'emf-bg-white'}`}>
            {filteredItems.length === 0 && query !== '' ? (
              <div className="emf-relative emf-cursor-default emf-select-none emf-px-4 py-2 emf-text-gray-700">
                Nothing found.
              </div>
            ) : (
              filteredItems.map((m, idx: number) => (
                <ComboboxOption
                  key={idx}
                  className={({ active }) => `emf-relative emf-cursor-default emf-select-none emf-pt-2 emf-pb-1 emf-px-4 ${active ? '!emf-bg-primary emf-opacity-80 emf-text-white' : theme == 'dark' ? 'emf-bg-dark-50' : 'emf-text-gray-900'}`} value={m.value}>
                  {({ selected }) => (
                    <label className="emf-flex emf-items-center">
                      <input type="checkbox" value={m.id} checked={m.selected} className="emf-w-4 emf-h-4 emf-accent-checked" onChange={(e) => handleBoolean(e, m)} />
                      <span className={`emf-ml-1 emf-block emf-truncate ${selected ? 'emf-font-medium' : 'emf-font-normal'}`}>
                        {m.label}
                      </span>
                    </label>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </Transition>
      </div>
    </Combobox>
  )
}

const WidgetExpandMenu = (props: IEmfWidget) => {
  let onClick = () => {
    props.onExpand && props.onExpand(true);
  }
  return (
    <button onClick={onClick} className={`emf-widget-action-icon hover:emf-text-primary focus:emf-outline-none ${props.config.theme == 'dark' ? 'emf-text-white' : ''}`}>
      <EmfExpandIcon title="Expand" className="emf-h-4 emf-w-4 emf-cursor-pointer hover:emf-text-primary focus:emf-text-primary focus-visible:emf-text-primary" />
    </button>
  )
}

const WidgetCollapseMenu = (props: IEmfWidget) => {
  let onClick = () => {
    props.onExpand && props.onExpand(false);
  }
  return (
    <button onClick={onClick} className={`emf-widget-action-icon hover:emf-text-primary focus:emf-outline-none ${props.config.theme == 'dark' ? 'emf-text-white' : ''}`}>
      <EmfCollapseIcon title="Collapse" className="emf-h-4 emf-w-4 emf-cursor-pointer hover:emf-text-primary focus:emf-text-primary focus-visible:emf-text-primary" />
    </button>
  )
}

interface WidgetPopupProps {
  props: IEmfWidget;
  theme: EmfWidgetThemeType;
  open: boolean;
  children: any;
  overlay?: boolean;
  onClose: () => void
}
const WidgetPopup = ({ props, theme, open, children, overlay, onClose }: WidgetPopupProps) => {
  if (overlay == null) { overlay = true }
  if (!open) return children;
  return (
    <>
      <div className="emf-widget-popout-overlay" onClick={onClose}>
        <div className="emf-widget-popout-content" onClick={(e) => e.stopPropagation()}> {/* Prevent closing on content click */}
          <button className="emf-widget-popout-close" onClick={onClose}>X</button>
          {children}
        </div>
      </div>
    </>
  );
};


interface WidgetTruncatedTextProps {
  text: string;
  classes?: string;
}
const WidgetTruncatedText = ({ text, classes }: WidgetTruncatedTextProps) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el && el.scrollWidth > el.clientWidth) {
      setIsTruncated(true);
    }
  }, [text]);

  return (
    <div
      ref={textRef}
      className={`emf-max-w-full emf-truncate ${classes ? classes : ''}`}
      title={isTruncated ? text : ''}>
      {text}
    </div>
  );
};


export { WidgetFilterMenu, WidgetSettingsMenu, WidgetRefreshMenu, WidgetCombobox, WidgetExpandMenu, WidgetCollapseMenu, WidgetVisualizationMenu, WidgetPopup, WidgetTruncatedText };
